import { useLatest } from 'ahooks';
import { useRef } from 'react';
import { SliderInput } from '@meitu/saas-ui';
import { SimpleTitle } from '@meitu/saas-ui/es/title';
import { BaseToolbar } from '@meitu/saas-editor-ui/es/components/base-toolbar';
import { DividerLine } from '@meitu/saas-editor-ui/es/components/divider-line';
import { HeaderToolbar } from '@meitu/saas-editor-ui/es/components/header-toolbar';
import LabelAction from '@meitu/saas-editor-ui/es/components/label-action';
import { useListenActivePickLayerProps } from '@meitu/saas-editor-ui/es/hooks/use-listen-layer-props';
import ActiveTablePanel, {
    ActionInfo,
    ActiveTablePanelRef,
} from '@meitu/saas-editor-ui/es/panels/table-layer-panel/table-layer-panel';
import { checkFontIdIsTeamFontId } from '@meitu/xdesign-widgets';
import { UserBizSearchProps } from '@meitu/xdesign-widgets/dist/textFontFamily';
import { usePanelLayerId } from '@/hooks';
import { useLocaleStore } from '@/i18n';
import { editor, type IEditor } from '@/instance';
import { useRecordModel } from '@/store/models/record-model';
import { useSearchModel } from '@/store/models/search-model';
import { useUserInfoModel } from '@/store/models/user-info-model';
import { Entrance } from '@/types';
import { openVipModal } from '@/utils/login';
import { getLayerType, HandleHbLayersFunc } from '@/utils/stat-track';
import './table-panel.less';

const trackFirstId = 7;

const OpacityFeature: React.FC<{
    editor: IEditor;
    id: string;
}> = ({ editor, id }) => {
    const { $t } = useLocaleStore();
    const originOpacity = useRef<number | null>(null);

    const opacity = useListenActivePickLayerProps(
        editor,
        (layer) => {
            if (!layer) return null;
            return layer.opacity;
        },
        true,
    );

    if (opacity === null) return null;

    return (
        <div className="c-brush-panel__item opacity blod" style={{ marginBottom: 16 }}>
            <LabelAction title={$t((v) => v.panel.tablePanel.opacity)} align>
                <SliderInput
                    value={+(opacity * 100).toFixed(0)}
                    max={100}
                    min={0}
                    showStepBtn
                    type="horizontal"
                    onChange={(val: number) => {
                        val = val / 100;
                        if (originOpacity.current === null) {
                            originOpacity.current = opacity;
                        }
                        editor.layer.setOpacity(id, val);
                    }}
                    onAfterChange={(val: number) => {
                        val = val / 100;
                        editor.layer.setOpacity(id, val, originOpacity.current);
                        originOpacity.current = null;
                    }}
                />
            </LabelAction>
        </div>
    );
};

const TablePanel = () => {
    const { $t } = useLocaleStore();

    const activeTablePanelRef = useRef<ActiveTablePanelRef>(null);
    const currentRecordId = useRecordModel((state) => state.recordInfo.id);
    const isVipUser = useUserInfoModel((state) => state.userInfo?.is_vip ?? false);
    const isSearchRef = useLatest(useSearchModel((state) => state.is_search));

    const layerId = usePanelLayerId()!;
    const stageType = editor.services.editor.getCanvasType();

    const textTrack = (props: { secondId: number; isUse?: boolean; isClick?: boolean }) => {
        const activePickLayer = editor.services.frame.getActiveLayer();
        HandleHbLayersFunc({
            ...props,
            firstId: trackFirstId,
            layerType: activePickLayer ? getLayerType(activePickLayer).layerType : 0,
            recordId: currentRecordId ? `${currentRecordId}` : '',
            canvas_type: stageType,
        });
    };

    const onAction = (actionInfo: ActionInfo) => {
        if ((actionInfo as any).target === 'onShowBrandColorsTab') {
            window._stat_track?.('hb_layers_func_click', {
                func_id: '7_107',
            });
            return;
        }

        let doClickTrack = true;
        let secondId: number | null = null;
        let textEditTrackData: Record<any, any> | null = null;
        switch (actionInfo.target) {
            case 'textAutoWrap':
                secondId = 55;
                break;
            case 'tablePreset':
                if (actionInfo.data) {
                    textTrack({
                        secondId: 56,
                        [actionInfo.data]: true,
                    });
                } else {
                    secondId = 56;
                }
                break;
            case 'rowOrColCount':
                secondId = 57;
                window._stat_track('hb_text_edit_yes_data', {
                    [actionInfo.type === 'row' ? 'rows' : 'columns']: actionInfo.data,
                });
                break;
            case 'cellBackground':
                {
                    const isBrandColor = actionInfo.data.isBrandColor;
                    secondId = isBrandColor ? 107 : 58;
                    if (isBrandColor) {
                        doClickTrack = false;
                    }
                    window._stat_track('hb_text_edit_yes_data', {
                        cells_colour: actionInfo.data.value,
                    });
                }
                break;
            case 'border':
                {
                    const key = {
                        mode: 'frame',
                        color: 'frame_colour',
                        style: 'line',
                        width: 'line_size',
                    }[actionInfo.type];
                    const isBrandColor = (actionInfo.data as any)?.isBrandColor ?? false;
                    secondId = isBrandColor ? 107 : 59;
                    if (isBrandColor) {
                        doClickTrack = false;
                    }
                    window._stat_track('hb_text_edit_yes_data', {
                        [key]: actionInfo.data,
                    });
                }
                break;
            case 'mergeCells':
                secondId = 60;
                break;
            case 'cancelMergeCell':
                secondId = 80;
                break;
            case 'textFontFamily':
                textEditTrackData = {
                    样式字体: actionInfo.data.id,
                    is_search: isSearchRef.current,
                };
                textTrack({
                    secondId: checkFontIdIsTeamFontId(actionInfo.data.id ?? 0) ? 107 : 7,
                    isUse: true,
                });
                break;
            case 'textFontFamilyDropdownVisible':
                textTrack({
                    secondId: 7,
                    isClick: true,
                });
                break;
            case 'fontSize':
                textEditTrackData = {
                    样式字号: actionInfo.data,
                    is_search: isSearchRef.current,
                };
                textTrack({
                    secondId: 8,
                    isUse: true,
                });
                break;
            case 'fontSizeClick':
                textTrack({
                    secondId: 8,
                    isClick: true,
                });
                break;
            case 'textColor':
                {
                    const isBrandColor = actionInfo.data.isBrandColor;
                    if (isBrandColor) {
                        doClickTrack = false;
                    }
                    doClickTrack = false;
                    textEditTrackData = {
                        样式字号: actionInfo.data.value,
                        is_search: isSearchRef.current,
                    };
                    textTrack({
                        secondId: isBrandColor ? 107 : 2,
                        isUse: true,
                        isClick: !isBrandColor,
                    });
                }
                break;
            case 'textAttr':
                {
                    textEditTrackData = {
                        is_search: isSearchRef.current,
                    };
                    const key = {
                        bold: '样式粗体',
                        italic: '样式斜体',
                        underline: '样式下划线',
                        lineThrough: '样式删除线',
                    }[actionInfo.type];
                    textEditTrackData[key] = actionInfo.data ? '是' : '否';
                    textTrack({
                        secondId: {
                            bold: 9,
                            italic: 10,
                            underline: 11,
                            lineThrough: 12,
                        }[actionInfo.type],
                        isUse: true,
                        isClick: true,
                    });
                }
                break;
            case 'textAlign':
                textEditTrackData = {
                    is_search: isSearchRef.current,
                };
                switch (actionInfo.type) {
                    case 'left':
                    case 'right':
                    case 'center':
                    case 'justify':
                        textEditTrackData['样式水平对齐'] = {
                            left: '左对齐',
                            right: '右对齐',
                            center: '居中对齐',
                            justify: '两端对齐',
                        }[actionInfo.type];
                        break;
                    case 'top':
                    case 'vertical-center':
                    case 'bottom':
                        textEditTrackData['样式竖直对齐'] = {
                            top: '顶对齐',
                            bottom: '底对齐',
                            'vertical-center': '垂直居中',
                        }[actionInfo.type];
                        break;
                }
                textTrack({
                    secondId: 13,
                    isUse: true,
                    isClick: true,
                });
                break;
        }
        if (secondId !== null) {
            const activePickLayer = editor.services.frame.getActiveLayer();
            HandleHbLayersFunc({
                firstId: trackFirstId,
                secondId,
                isClick: doClickTrack,
                isUse: true,
                recordId: currentRecordId ? `${currentRecordId}` : '',
                canvas_type: stageType,
                layerType: activePickLayer ? getLayerType(activePickLayer).layerType : 0,
            });
        }
        if (textEditTrackData !== null) {
            window._stat_track('hb_text_edit_yes_data', textEditTrackData);
        }
    };

    return (
        <ActiveTablePanel
            ref={activeTablePanelRef}
            editor={editor}
            isVipUser={isVipUser}
            handleShowVip={() =>
                openVipModal({
                    entrance: Entrance.Material,
                })
            }
            onAction={onAction}
            textFontFamilyComponentUserBizSearchProps={UserBizSearchProps}
        >
            {({ TablePanel, TextPanel }) => (
                <div>
                    <HeaderToolbar id={layerId} editor={editor} style={{ padding: 16 }} />

                    <div style={{ padding: '0 16px' }}>{TablePanel}</div>

                    <DividerLine />

                    <div style={{ padding: '0 16px' }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <SimpleTitle text={$t((v) => v.panel.tablePanel.textStyle)} fontSize={14} />
                        </div>
                        <div style={{ marginTop: 8 }}>{TextPanel}</div>
                    </div>

                    <DividerLine />

                    {/* 图形透明度 */}
                    <OpacityFeature editor={editor} id={layerId} />

                    <DividerLine />

                    {/* 通用功能 */}
                    <BaseToolbar id={layerId} editor={editor} style={{ padding: '0 16px 16px' }} />
                </div>
            )}
        </ActiveTablePanel>
    );
};

TablePanel.displayName = 'TablePanel';

export default TablePanel;
